import * as THREE from "three";

export class MeshBatcher
{
  public static readonly HIDDEN_LAYER = 30;
  public static readonly HIDDEN_MASK = ( 1 << MeshBatcher.HIDDEN_LAYER ) >>> 0;
  private static readonly MIN_GROUP = 2;
  private static readonly IDLE_FRAMES_BEFORE_DISPOSE = 120;

  private static enabled = MeshBatcher.readEnabled();
  public static batch_projectiles = false;

  private static groups = new Map<string, Group>();
  private static entries = new WeakMap<THREE.Mesh, Entry>();
  private static hidden = new Set<Entry>();
  private static collected = new Map<string, Entry[]>();
  private static stack: THREE.Object3D[] = [];
  private static frame = 0;

  private static readonly proj_screen = new THREE.Matrix4();
  private static readonly view_frustum = new THREE.Frustum();
  private static readonly view_sphere = new THREE.Sphere();
  private static readonly CULL_MARGIN = 1.5;
  private static culled = 0;

  private static last_stats = { sources: 0, groups: 0, batched: 0, culled: 0, draws_saved: 0, sync_ms: 0 };

  public static isEnabled(): boolean
  {
    return this.enabled;
  }

  public static setEnabled( on: boolean ): void
  {
    this.enabled = on;
    try { localStorage.setItem( "bb_mesh_batching", on ? "1" : "0" ); } catch {}
  }

  public static stats(): typeof MeshBatcher.last_stats
  {
    return { ...this.last_stats };
  }

  public static sync( scene: THREE.Scene, camera: THREE.Camera ): void
  {
    const started = performance.now();
    scene.updateMatrixWorld();
    if ( camera.parent === null && camera.matrixWorldAutoUpdate )
      camera.updateMatrixWorld();
    this.frame++;

    if ( !this.enabled )
    {
      this.releaseAll();
      return;
    }

    this.collected.forEach( clearList );

    this.proj_screen.multiplyMatrices( camera.projectionMatrix, camera.matrixWorldInverse );
    this.view_frustum.setFromProjectionMatrix( this.proj_screen );
    this.culled = 0;
    const sources = this.collect( scene );

    let batched = 0;
    let draws_saved = 0;
    let group_count = 0;

    this.collected.forEach( ( list, key ) =>
    {
      const group = this.groups.get( key );

      if ( list.length === 0 || ( list.length < this.MIN_GROUP && !group && list[0].custom_shader ) )
      {
        if ( group )
          group.idle( this.frame );
        else if ( list.length === 0 )
          this.collected.delete( key );
        return;
      }

      this.sortFrontToBack( list );
      const target = group ?? this.createGroup( key, list[0], scene );
      target.write( list, this.frame, scene );

      for ( let i = 0; i < list.length; i++ )
      {
        const entry = list[i];
        entry.mesh.layers.mask = this.HIDDEN_MASK;
        entry.hidden_frame = this.frame;
        this.hidden.add( entry );
      }

      batched += list.length;
      draws_saved += list.length - 1;
      group_count++;
    } );

    for ( const entry of this.hidden )
    {
      if ( entry.hidden_frame === this.frame )
        continue;
      this.restore( entry );
      this.hidden.delete( entry );
    }

    this.groups.forEach( ( group, key ) =>
    {
      if ( group.last_used !== this.frame )
        group.idle( this.frame );

      if ( this.frame - group.last_used > this.IDLE_FRAMES_BEFORE_DISPOSE )
      {
        group.dispose();
        this.groups.delete( key );
        this.collected.delete( key );
      }
    } );

    const stats = this.last_stats;
    stats.sources = sources;
    stats.groups = group_count;
    stats.batched = batched;
    stats.culled = this.culled;
    stats.draws_saved = draws_saved;
    stats.sync_ms = performance.now() - started;
  }

  private static sortFrontToBack( list: Entry[] ): void
  {
    const p = this.proj_screen.elements;

    for ( let i = 0; i < list.length; i++ )
    {
      const entry = list[i];
      const c = entry.center;
      const m = entry.mesh.matrixWorld.elements;
      const x = m[0] * c.x + m[4] * c.y + m[8] * c.z + m[12];
      const y = m[1] * c.x + m[5] * c.y + m[9] * c.z + m[13];
      const z = m[2] * c.x + m[6] * c.y + m[10] * c.z + m[14];
      const w = p[3] * x + p[7] * y + p[11] * z + p[15];
      entry.depth = ( p[2] * x + p[6] * y + p[10] * z + p[14] ) / w;
    }

    sortEntries( list, 0, list.length - 1 );
  }

  private static collect( root: THREE.Object3D ): number
  {
    let sources = 0;
    const stack = this.stack;
    stack.length = 0;
    stack.push( root );

    while ( stack.length > 0 )
    {
      const object = stack.pop()!;
      if ( !object.visible || ( object.userData.noBatch === true && !this.batch_projectiles ) )
        continue;

      if ( ( object as THREE.Mesh ).isMesh )
      {
        const entry = this.entryOf( object as THREE.Mesh );
        if ( entry !== null && !this.inView( entry ) )
        {
          entry.mesh.layers.mask = this.HIDDEN_MASK;
          entry.hidden_frame = this.frame;
          this.hidden.add( entry );
          this.culled++;
        }
        else if ( entry !== null )
        {
          let list = this.collected.get( entry.key );
          if ( !list )
          {
            list = [];
            this.collected.set( entry.key, list );
          }
          list.push( entry );
          sources++;
        }
      }

      const children = object.children;
      for ( let i = children.length - 1; i >= 0; i-- )
        stack.push( children[i] );
    }

    return sources;
  }

  private static inView( entry: Entry ): boolean
  {
    const mesh = entry.mesh;
    if ( !mesh.frustumCulled )
      return true;

    this.view_sphere.center.copy( entry.center ).applyMatrix4( mesh.matrixWorld );
    this.view_sphere.radius = entry.radius * mesh.matrixWorld.getMaxScaleOnAxis() + this.CULL_MARGIN;
    return this.view_frustum.intersectsSphere( this.view_sphere );
  }

  private static entryOf( mesh: THREE.Mesh ): Entry | null
  {
    const material = mesh.material;
    if ( Array.isArray( material ) || !material.visible || material.transparent )
      return null;

    let entry = this.entries.get( mesh );

    if ( entry === undefined )
    {
      if ( mesh.userData.meshBatcherGroup === true || ( mesh.userData.noBatch && !this.batch_projectiles )
        || ( mesh as THREE.InstancedMesh ).isInstancedMesh || ( mesh as THREE.SkinnedMesh ).isSkinnedMesh )
        return null;

      entry = new Entry( mesh );
      this.entries.set( mesh, entry );
    }

    let mask = mesh.layers.mask;
    if ( mask === this.HIDDEN_MASK )
      mask = entry.original_mask;
    else
      entry.original_mask = mask;

    const geometry = mesh.geometry as THREE.BufferGeometry;
    if ( entry.geometry !== geometry || entry.material !== material || entry.cast !== mesh.castShadow
      || entry.receive !== mesh.receiveShadow || entry.mask !== mask || entry.render_order !== mesh.renderOrder
      || entry.depth_material !== mesh.customDepthMaterial )
    {
      entry.geometry = geometry;
      entry.material = material;
      entry.cast = mesh.castShadow;
      entry.receive = mesh.receiveShadow;
      entry.mask = mask;
      entry.render_order = mesh.renderOrder;
      entry.depth_material = mesh.customDepthMaterial;
      entry.custom_shader = ( material as THREE.ShaderMaterial ).isShaderMaterial === true;
      entry.batchable = !( geometry as THREE.InstancedBufferGeometry ).isInstancedBufferGeometry
        && Object.keys( geometry.morphAttributes ).length === 0;
      if ( geometry.boundingSphere === null )
        geometry.computeBoundingSphere();
      entry.center = geometry.boundingSphere!.center;
      entry.radius = geometry.boundingSphere!.radius;
      entry.key = `${geometry.id}|${material.uuid}|${mesh.castShadow ? 1 : 0}${mesh.receiveShadow ? 1 : 0}|${mask}|${mesh.renderOrder}|${mesh.customDepthMaterial?.uuid ?? ""}`;
    }

    if ( !entry.batchable )
      return null;

    const e = mesh.matrixWorld.elements;
    const det = e[0] * ( e[5] * e[10] - e[6] * e[9] ) - e[4] * ( e[1] * e[10] - e[2] * e[9] ) + e[8] * ( e[1] * e[6] - e[2] * e[5] );
    if ( det <= 0 )
      return null;

    return entry;
  }

  private static createGroup( key: string, sample: Entry, scene: THREE.Scene ): Group
  {
    const group = new Group( sample, sample.original_mask );
    this.groups.set( key, group );
    scene.add( group.mesh );
    return group;
  }

  private static restore( entry: Entry ): void
  {
    if ( entry.mesh.layers.mask === this.HIDDEN_MASK )
      entry.mesh.layers.mask = entry.original_mask;
  }

  public static restoreHidden(): void
  {
    for ( const entry of this.hidden )
      this.restore( entry );
    this.hidden.clear();
  }

  private static releaseAll(): void
  {
    this.restoreHidden();

    for ( const group of this.groups.values() )
      group.dispose();
    this.groups.clear();
    this.collected.clear();
    this.last_stats = { sources: 0, groups: 0, batched: 0, culled: 0, draws_saved: 0, sync_ms: 0 };
  }

  private static readEnabled(): boolean
  {
    try { return localStorage.getItem( "bb_mesh_batching" ) !== "0"; } catch { return true; }
  }
}

class Entry
{
  public geometry: THREE.BufferGeometry | null = null;
  public material: THREE.Material | null = null;
  public cast = false;
  public receive = false;
  public mask = -1;
  public render_order = 0;
  public depth_material: THREE.Material | undefined = undefined;
  public original_mask = 1;
  public batchable = false;
  public custom_shader = false;
  public key = "";
  public center = new THREE.Vector3();
  public radius = 0;
  public depth = 0.5;
  public hidden_frame = -1;

  constructor( public readonly mesh: THREE.Mesh ) {}
}

function sortEntries( list: Entry[], lo: number, hi: number ): void
{
  while ( hi - lo > 16 )
  {
    const pivot = list[( lo + hi ) >> 1];
    let i = lo, j = hi;
    while ( i <= j )
    {
      while ( byDepth( list[i], pivot ) < 0 ) i++;
      while ( byDepth( list[j], pivot ) > 0 ) j--;
      if ( i <= j )
      {
        const tmp = list[i];
        list[i] = list[j];
        list[j] = tmp;
        i++;
        j--;
      }
    }
    if ( j - lo < hi - i )
    {
      sortEntries( list, lo, j );
      lo = i;
    }
    else
    {
      sortEntries( list, i, hi );
      hi = j;
    }
  }

  for ( let i = lo + 1; i <= hi; i++ )
  {
    const entry = list[i];
    let j = i - 1;
    while ( j >= lo && byDepth( list[j], entry ) > 0 )
    {
      list[j + 1] = list[j];
      j--;
    }
    list[j + 1] = entry;
  }
}

function clearList( list: Entry[] ): void
{
  list.length = 0;
}

function byDepth( a: Entry, b: Entry ): number
{
  return a.depth < b.depth ? -1 : a.depth > b.depth ? 1 : a.mesh.id - b.mesh.id;
}

class Group
{
  public mesh: THREE.InstancedMesh;
  public last_used = 0;
  private capacity = 0;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.Material;
  private readonly cast: boolean;
  private readonly receive: boolean;
  private readonly render_order: number;
  private readonly depth_material: THREE.Material | undefined;

  constructor( sample: Entry, private readonly mask: number )
  {
    this.geometry = sample.geometry!;
    this.material = sample.material!;
    this.cast = sample.cast;
    this.receive = sample.receive;
    this.render_order = sample.render_order;
    this.depth_material = sample.depth_material;
    this.mesh = this.build( 16 );
  }

  public write( list: Entry[], frame: number, scene: THREE.Scene ): void
  {
    if ( list.length > this.capacity )
    {
      let capacity = this.capacity;
      while ( capacity < list.length )
        capacity *= 2;

      const old = this.mesh;
      this.mesh = this.build( capacity );
      old.removeFromParent();
      old.dispose();
    }

    if ( this.mesh.parent !== scene )
      scene.add( this.mesh );

    const array = this.mesh.instanceMatrix.array as Float32Array;
    let changed = list.length !== this.mesh.count;
    for ( let i = 0; i < list.length; i++ )
    {
      const elements = list[i].mesh.matrixWorld.elements;
      const offset = i * 16;
      for ( let k = 0; k < 16; k++ )
      {
        const value = elements[k];
        if ( array[offset + k] !== value )
        {
          array[offset + k] = value;
          changed = true;
        }
      }
    }

    this.mesh.count = list.length;
    this.mesh.visible = true;
    this.mesh.layers.mask = this.mask;
    if ( changed )
    {
      this.mesh.instanceMatrix.clearUpdateRanges();
      this.mesh.instanceMatrix.addUpdateRange( 0, list.length * 16 );
      this.mesh.instanceMatrix.needsUpdate = true;
    }
    this.last_used = frame;
  }

  public idle( frame: number ): void
  {
    if ( this.last_used === frame )
      return;
    this.mesh.count = 0;
    this.mesh.visible = false;
  }

  public dispose(): void
  {
    this.mesh.removeFromParent();
    this.mesh.dispose();
  }

  private build( capacity: number ): THREE.InstancedMesh
  {
    this.capacity = capacity;

    const mesh = new THREE.InstancedMesh( this.geometry, this.material, capacity );
    mesh.instanceMatrix.setUsage( THREE.DynamicDrawUsage );
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.matrixWorldAutoUpdate = false;
    mesh.castShadow = this.cast;
    mesh.receiveShadow = this.receive;
    mesh.renderOrder = this.render_order;
    mesh.customDepthMaterial = this.depth_material;
    mesh.layers.mask = this.mask;
    mesh.userData.meshBatcherGroup = true;
    mesh.name = "mesh_batcher_group";
    return mesh;
  }
}
